import React from "react";
import Cropper from "react-easy-crop";
import { PlusCircleIcon, MinusCircleIcon } from "@heroicons/react/24/solid";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmButton, CancelButton } from "@/components/common/buttons";
interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropImageModalProps {
  open: boolean;
  onClose: () => void;
  imageToCrop: string | null;
  imageType: "profile" | "background";
  crop: { x: number; y: number };
  setCrop: (crop: { x: number; y: number }) => void;
  zoom: number;
  setZoom: (zoom: number) => void;
  onCropComplete: (croppedArea: any, croppedAreaPixels: any) => void;
  onSave: () => void;
  isSaving: boolean;
}

const CropImageModal: React.FC<CropImageModalProps> = ({
  open,
  onClose,
  imageToCrop,
  imageType,
  crop,
  setCrop,
  zoom,
  setZoom,
  onCropComplete,
  onSave,
  isSaving,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader className="pb-4 border-b">
          <DialogTitle className="text-xl 2xl:text-2xl font-semibold text-neutral-900">
            Crop Image
          </DialogTitle>
          <DialogDescription className="text-xs 2xl:text-sm text-neutral-500">
            Edit your profile image to the desired size
          </DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-[300px]">
          {imageToCrop && (
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={imageType === "profile" ? 1 : 16 / 9}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropShape={imageType === "profile" ? "round" : "rect"}
              showGrid={false}
              objectFit="contain"
              classes={{
                containerClassName: "rounded-lg",
              }}
            />
          )}
        </div>
        <div className="px-4">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => setZoom(zoom - 0.1)}
              className="shrink-0 text-indigo-700 hover:text-indigo-800 bg-neutral-100 rounded-full w-8 h-8 flex items-center justify-center"
            >
              <MinusCircleIcon className="w-4 h-4" />
            </button>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.01}
              aria-label="Zoom"
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full h-1 bg-indigo-100 rounded-full appearance-none cursor-pointer range-lg
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:w-4  
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-indigo-600
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-moz-range-thumb]:h-4
              [&::-moz-range-thumb]:w-4
              [&::-moz-range-thumb]:rounded-full 
              [&::-moz-range-thumb]:bg-indigo-600
              [&::-moz-range-thumb]:border-0
              [&::-moz-range-thumb]:cursor-pointer
              hover:[&::-webkit-slider-thumb]:bg-indigo-700
              hover:[&::-moz-range-thumb]:bg-indigo-700"
            />
            <button
              onClick={() => setZoom(zoom + 0.1)}
              className="shrink-0 text-indigo-700 hover:text-indigo-800 bg-neutral-100 rounded-full w-8 h-8 flex items-center justify-center"
            >
              <PlusCircleIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <CancelButton onClick={onClose} disabled={isSaving} />
          <ConfirmButton
            onClick={onSave}
            isLoading={isSaving}
            disabled={isSaving}
            title="Save"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CropImageModal;
